import { CUSTOM_LABEL_PREFIX } from '@this+that/constants';
import { LabelId, MessagingPlatform, type ViewType } from '@this+that/models';

const EXCLUDED_LABELS = new Set([
  LabelId.UNREAD,
  LabelId.IMPORTANT,
  LabelId.STARRED,
  LabelId.YELLOW_STAR,
]);

const CATEGORY_LABELS = new Set([
  LabelId.CATEGORY_PERSONAL,
  LabelId.CATEGORY_PROMOTIONS,
  LabelId.CATEGORY_SOCIAL,
  LabelId.CATEGORY_UPDATES,
  LabelId.CATEGORY_FORUMS,
]);

const isExcludedLabel = (labelId: string): boolean => {
  return EXCLUDED_LABELS.has(labelId as LabelId);
};

const getCategoryLabels = (labelIds: string[]): string[] => {
  return labelIds.filter((id) => CATEGORY_LABELS.has(id as LabelId));
};

const getCustomLabels = (labelIds: string[]): string[] => {
  return labelIds.filter((id) => id.startsWith(CUSTOM_LABEL_PREFIX));
};

const isInExcludedFolder = (labelIds: string[]): boolean => {
  return (
    labelIds.includes(LabelId.TRASH) ||
    labelIds.includes(LabelId.SPAM) ||
    labelIds.includes(LabelId.DRAFT)
  );
};

interface CalculateViewsParams {
  labelIds?: string[];
  platform: MessagingPlatform;
}

export const calculateViews = ({
  labelIds,
  platform,
}: CalculateViewsParams): string[] => {
  // Ensure labelIds is always an array (handles both null and undefined)
  const safeLabelIds = labelIds ?? [];

  // Filter out excluded labels (UNREAD, STARRED, IMPORTANT)
  const relevantLabels = safeLabelIds.filter((id) => !isExcludedLabel(id));

  // Handle emails with no relevant labels
  if (!relevantLabels.length) {
    // Outlook: archived emails (empty [] or only [UNREAD]) appear in ALL_EMAILS
    // Gmail: emails with only metadata labels don't appear in any view
    return platform === MessagingPlatform.OUTLOOK ? ['ALL_EMAILS'] : [];
  }

  // Convert to Set for O(1) lookups
  const labelSet = new Set(relevantLabels);

  const hasDraft = labelSet.has(LabelId.DRAFT);
  const hasInbox = labelSet.has(LabelId.INBOX);
  const hasSent = labelSet.has(LabelId.SENT);
  const hasTrash = labelSet.has(LabelId.TRASH);
  const hasSpam = labelSet.has(LabelId.SPAM);

  // Get categories once
  const categories = getCategoryLabels(relevantLabels);
  const hasCategory = categories.length > 0;

  const views = new Set<ViewType>();

  if (hasDraft) {
    views.add('DRAFT');
  }

  if (hasSent) {
    views.add('SENT');
  }

  if (hasTrash) {
    views.add('TRASH');
  }

  if (hasSpam) {
    views.add('SPAM');
  }

  // Check inbox-related views
  if (hasInbox) {
    if (!categories?.length) {
      // No categories = PRIMARY
      views.add('PRIMARY');
    } else if (categories.length === 1) {
      // Single category determines the view
      const category = categories[0];
      switch (category) {
        case LabelId.CATEGORY_PROMOTIONS:
          views.add('PROMOTIONS');
          break;
        case LabelId.CATEGORY_SOCIAL:
          views.add('SOCIAL');
          break;
        case LabelId.CATEGORY_UPDATES:
          views.add('UPDATES');
          break;
        case LabelId.CATEGORY_FORUMS:
          views.add('FORUMS');
          break;
        case LabelId.CATEGORY_PERSONAL:
          views.add('PRIMARY');
          break;
        default:
          // No-op for unknown categories
          break;
      }
    }
    // Multiple categories = no category-specific view added
  }

  // Custom Labels: Any Label_* that isn't SPAM or TRASH
  const customLabels = getCustomLabels(relevantLabels);
  customLabels.forEach((label) => views.add(label as ViewType));

  const isCustomFolder =
    customLabels.length > 0 && platform === MessagingPlatform.OUTLOOK;

  // ALL_EMAILS: Include if:
  // 1. INBOX or SENT present
  // 2. Custom folder present in Outlook (identified by Label_* prefix)
  // 3. CATEGORY present without excluded folders (TRASH/SPAM/DRAFT)
  if (
    hasInbox ||
    hasSent ||
    isCustomFolder ||
    (hasCategory && !isInExcludedFolder(safeLabelIds))
  ) {
    views.add('ALL_EMAILS');
  }

  return Array.from(views);
};
